import React from 'react';
import PropTypes from 'prop-types';

const EMPTY_VALUE = '—';

const getValue = (value) => (value === undefined || value === null || value === '' ? EMPTY_VALUE : value);

const joinPresentValues = (values, separator = ' ') => (
  values.filter((value) => value !== undefined && value !== null && value !== '').join(separator)
);

const joinValues = (values, separator = ' ') => joinPresentValues(values, separator) || EMPTY_VALUE;

const DetailItem = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="mb-[4px] text-[11px] font-semibold uppercase tracking-wide text-romanSilver">
      {label}
    </dt>
    <dd className="break-words text-[13px] font-medium text-dark dark:text-secondary">
      {getValue(value)}
    </dd>
  </div>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DetailItem.defaultProps = {
  value: undefined,
};

const CoOrderDetailsRow = ({ order }) => {
  const { _id: orderId } = order;
  const customer = joinValues([
    order.order_shipping_first_name,
    order.order_shipping_last_name,
  ]);
  const address = joinValues([
    joinPresentValues([order.order_shipping_address, order.order_shipping_address_line2], ', '),
    joinPresentValues([order.order_shipping_city, order.order_shipping_state, order.order_shipping_zip], ', '),
    order.order_shipping_country,
  ], ' · ');
  const dimensions = joinValues([
    order.order_item_shipping_length,
    order.order_item_shipping_width,
    order.order_item_shipping_height,
  ], ' × ');

  return (
    <tr
      id={`order-details-${orderId}`}
      data-detail-row="true"
      data-testid={`order-details-${orderId}`}
    >
      <td colSpan={100} className="bg-input-bg/40 px-[18px] py-[14px] dark:bg-[#1b263b]">
        <dl className="grid max-w-[960px] grid-cols-1 gap-x-[20px] gap-y-[12px] sm:grid-cols-2 lg:grid-cols-3">
          <DetailItem label="Marketplace" value={order.order_marketplace_name} />
          <DetailItem label="Marketplace account" value={order.order_marketplace_account} />
          <DetailItem label="Marketplace order #" value={order.order_marketplace_number} />
          <DetailItem label="Listing title" value={order.order_listings_title} />
          <DetailItem label="Customer" value={customer} />
          <DetailItem label="Phone" value={order.order_shipping_phone} />
          <DetailItem label="Email" value={order.order_shipping_email} />
          <DetailItem label="Shipping service" value={order.order_shipping_service} />
          <div className="min-w-0 sm:col-span-2">
            <DetailItem label="Shipping address" value={address} />
          </div>
          <DetailItem label="Package dimensions (L × W × H)" value={dimensions} />
          <DetailItem label="Package weight" value={order.order_item_shipping_weight} />
          <div className="min-w-0 sm:col-span-2">
            <DetailItem label="Checkout message" value={order.order_checkout_message} />
          </div>
          <div className="min-w-0 sm:col-span-2">
            <DetailItem label="Note to client" value={order.order_notes_to_client} />
          </div>
        </dl>
      </td>
    </tr>
  );
};

CoOrderDetailsRow.propTypes = {
  order: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    order_marketplace_name: PropTypes.string,
    order_marketplace_account: PropTypes.string,
    order_marketplace_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_listings_title: PropTypes.string,
    order_shipping_first_name: PropTypes.string,
    order_shipping_last_name: PropTypes.string,
    order_shipping_phone: PropTypes.string,
    order_shipping_email: PropTypes.string,
    order_shipping_service: PropTypes.string,
    order_shipping_address: PropTypes.string,
    order_shipping_address_line2: PropTypes.string,
    order_shipping_city: PropTypes.string,
    order_shipping_state: PropTypes.string,
    order_shipping_zip: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_shipping_country: PropTypes.string,
    order_item_shipping_length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_item_shipping_width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_item_shipping_height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_item_shipping_weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_checkout_message: PropTypes.string,
    order_notes_to_client: PropTypes.string,
  }).isRequired,
};

export default CoOrderDetailsRow;
